import React, { useState, useRef, useEffect } from "react";
import { ArrowRight, MapPin, Maximize2, PencilRuler, Wrench, Palette, Lightbulb, Truck, ClipboardList } from "lucide-react";
import { services, projects } from "../mock";

const iconMap = { PencilRuler, Wrench, Palette, Lightbulb, Truck, ClipboardList };

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export function Services() {
  return (
    <section id="services" className="section-pad bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="eyebrow mb-4">OUR SERVICES</p>
          <h2 className="heading-2 mb-4">Everything Your Stand Requires</h2>
          <p className="text-body text-lg">A complete exhibition service, coordinated through one dedicated team.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon];
            return (
              <div key={i} className="service-card bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col">
                <div className="relative h-48 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                  {s.tag && (
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-charcoal text-[11px] font-semibold tracking-wide px-2.5 py-1 rounded-full">
                      {s.tag}
                    </span>
                  )}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="w-12 h-12 rounded-xl bg-brand-soft flex items-center justify-center mb-4 -mt-11 relative bg-white shadow-sm border border-gray-100">
                    {Icon && <Icon className="text-brand" size={24} />}
                  </div>
                  <h3 className="font-display font-semibold text-charcoal text-xl mb-3">{s.title}</h3>
                  <p className="text-body mb-5 flex-1">{s.desc}</p>
                  <button onClick={() => scrollTo("contact")} className="link-arrow">
                    Learn more <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-14">
          <button onClick={() => scrollTo("contact")} className="btn-primary">
            Discuss Your Requirements <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

export function Projects() {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(6);
  const [selected, setSelected] = useState(null);
  const [photo, setPhoto] = useState(0);
  const dialog = useRef(null);
  const filtered = projects.filter(p => [p.title,p.client,p.location].join(" ").toLowerCase().includes(query.toLowerCase()));
  const photos = selected ? (selected.photos || [selected.image]) : [];
  useEffect(() => {
    if (selected && dialog.current && !dialog.current.open) dialog.current.showModal();
  }, [selected]);
  const open = p => {setSelected(p);setPhoto(Number.isInteger(p.cover) ? p.cover : 0);};
  const close = () => {dialog.current?.close();setSelected(null);};
  const step = amount => setPhoto(current => (current + amount + photos.length) % photos.length);
  return (
    <section id="work" className="section-pad bg-soft">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl"><p className="eyebrow mb-4">OUR WORK</p><h2 className="heading-2 mb-4">Exhibitions & Projects</h2><p className="text-body text-lg">Explore our stand designs and photo galleries from each show.</p></div>
          <label className="text-sm font-medium text-charcoal">Find a show<input type="search" value={query} onChange={e => {setQuery(e.target.value);setLimit(6);}} placeholder="Show, client or city" className="block mt-2 border border-gray-300 rounded-lg px-4 py-3 bg-white w-full md:w-64" /></label>
        </div>
        <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-8">
          {filtered.slice(0,limit).map((p,i) => {
            const count = (p.photos || [p.image]).length;
            return <article key={p.id || i} className="project-card bg-white border border-gray-200 rounded-2xl overflow-hidden group">
              <button onClick={() => open(p)} className="block w-full text-left relative h-56 overflow-hidden" aria-label={`View ${p.title} gallery, ${count} photos`}>
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                {!p.actual && <span className="absolute top-3 left-3 bg-black/55 text-white text-xs px-3 py-1 rounded-full">Illustrative</span>}
                <span className="absolute bottom-3 right-3 bg-white/95 text-charcoal text-xs font-semibold px-3 py-1.5 rounded-full">{count} {count === 1 ? 'photo' : 'photos'}</span>
              </button>
              <div className="p-6"><h3 className="font-display font-semibold text-charcoal text-xl mb-1.5">{p.title}</h3><p className="text-brand font-medium mb-3">{p.client}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">{p.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15}/>{p.location}</span>}{p.size && <span className="inline-flex items-center gap-1.5"><Maximize2 size={15}/>{p.size}</span>}</div>
                <p className="text-body mb-5">{p.desc}</p><button onClick={() => open(p)} className="link-arrow">View gallery <ArrowRight size={16}/></button>
              </div>
            </article>;
          })}
        </div>
        {filtered.length === 0 && <p className="text-body py-8">{query ? 'No shows match your search.' : 'Project galleries will appear here soon.'}</p>}
        {filtered.length > limit && <div className="text-center mt-10"><button className="btn-primary" onClick={() => setLimit(current => current + 6)}>Show more projects</button></div>}
      </div>
      <dialog ref={dialog} className="show-dialog" onCancel={() => setSelected(null)} onClose={() => setSelected(null)} onKeyDown={e => {if(e.key === 'ArrowRight'){e.preventDefault();step(1);}if(e.key === 'ArrowLeft'){e.preventDefault();step(-1);}}}>
        {selected && <div><div className="gallery-heading"><div><h2>{selected.title}</h2><p>{selected.client}{selected.location ? ` · ${selected.location}` : ''}</p></div><button onClick={close} className="gallery-close" aria-label="Close gallery">✕</button></div>
          <div className="gallery-image"><img src={photos[photo]} alt={`${selected.title} — photo ${photo+1}`} />{photos.length > 1 && <><button className="gallery-prev" onClick={() => step(-1)} aria-label="Previous photo">‹</button><button className="gallery-next" onClick={() => step(1)} aria-label="Next photo">›</button></>}</div>
          <p className="gallery-count">{photo+1} / {photos.length}</p>
          {photos.length > 1 && <div className="gallery-thumbnails">{photos.map((url,i) => <button key={url+i} onClick={() => setPhoto(i)} aria-label={`View photo ${i+1}`} aria-pressed={photo===i}><img src={url} alt="" loading="lazy" /></button>)}</div>}
          <p className="gallery-description">{selected.desc}</p>
        </div>}
      </dialog>
    </section>
  );
}
